import React from 'react';

import { AnalyticsEvent, pushAnalyticsEvent } from 'utils/analytics';

const logVideoEvent = ({ src, status, duration, currentTime, previousTime }) => {
    pushAnalyticsEvent(AnalyticsEvent.VIDEO_INTERACTION, {
        url: src,
        title: src.split('/').pop(),
        status,
        duration,
        currentTime,
        previousTime,
    });
};

const useVideoState = (customRef, onPlayClick) => {
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState(false);

    const innerRef = React.useRef();
    const videoRef = customRef || innerRef;

    const handleClick = () => {
        if (isPlaying) {
            videoRef.current.pause();
            setIsPlaying(false);
        } else {
            videoRef.current.play();
            setIsPlaying(true);
            setButtonClicked(true);
            onPlayClick(videoRef.current);
        }
    };

    const status = React.useRef('');
    const seekTriggered = React.useRef(false);
    const setSeekTriggered = newState => {
        seekTriggered.current = newState;
    };

    const previousTime = React.useRef(0);

    React.useEffect(() => {
        if (!videoRef?.current) {
            return;
        }

        const setStatus = newStatus => {
            status.current = newStatus;
        };

        const handlerPause = e => {
            const { readyState } = videoRef.current;
            const triggeredBySeek = readyState === 1;

            if (triggeredBySeek) {
                return;
            }

            setIsPlaying(false);
            setStatus('pause');

            logVideoEvent({
                src: e.target.currentSrc,
                status: status.current,
                duration: e.target.duration,
                currentTime: e.target.currentTime,
            });
        };

        const handlerStart = e => {
            if (seekTriggered.current) {
                setSeekTriggered(false);

                return;
            }

            setIsPlaying(true);
            setStatus('play');
            logVideoEvent({
                src: e.target.currentSrc,
                status: status.current,
                duration: e.target.duration,
                currentTime: e.target.currentTime,
            });
        };

        const handleEneded = e => {
            setIsPlaying(false);
            setStatus('complete');
            logVideoEvent({
                src: e.target.currentSrc,
                status: status.current,
                duration: e.target.duration,
                currentTime: e.target.currentTime,
            });
        };

        const handleSeeked = e => {
            setStatus('seek');
            setSeekTriggered(true);

            logVideoEvent({
                src: e.target.currentSrc,
                status: status.current,
                duration: e.target.duration,
                currentTime: e.target.currentTime,
                previousTime: previousTime.current,
            });
        };

        const handleSeeking = () => {
            if (current.played.length >= 1) {
                previousTime.current = current.played.end(current.played.length - 1);
            }
        };

        const { current } = videoRef;

        current.addEventListener('play', handlerStart);
        current.addEventListener('pause', handlerPause);
        current.addEventListener('ended', handleEneded);
        current.addEventListener('seeked', handleSeeked);
        current.addEventListener('seeking', handleSeeking);

        // eslint-disable-next-line consistent-return
        return () => {
            current.removeEventListener('play', handlerStart);
            current.removeEventListener('pause', handlerPause);
            current.removeEventListener('ended', handleEneded);
            current.removeEventListener('seeked', handleSeeked);
            current.removeEventListener('seeking', handleSeeking);
        };
    }, [videoRef]);

    return [isPlaying, handleClick, videoRef, buttonClicked, status.current];
};

export default useVideoState;
